'use client';

import { Box, Container, Flex, freddyTheme, Link, StickyBar } from '@coolblue-development/freddy';
import { Image } from '@coolblue-development/next-image/client';
import {
  SalesPrice,
  SalesPriceCurrent,
  SalesPriceFormer
} from '@coolblue-development/next-sales-price/client';
import { useTranslation } from '@i18n/useTranslation';
import { type ReactNode, type RefObject, useContext, useEffect } from 'react';

import { useIntersectionObserver } from '../hooks/useIntersectionObserver';
import type { Product } from '../typings/types';
import Cell from './Cell';
import { ComparisonContext } from './ComparisonContext';
import Row from './Row';

const StickyProductSummaryBar = ({
  products,
  scrollRef,
  buttons
}: {
  products: Product[];
  scrollRef: RefObject<HTMLDivElement>;
  buttons?: ReactNode;
}) => {
  const { isIntersecting, ref } = useIntersectionObserver({ initialIsIntersecting: true });
  const { setStickyBarVisible } = useContext(ComparisonContext);
  const { t } = useTranslation('webshop');

  useEffect(() => {
    setStickyBarVisible?.(!isIntersecting);
  }, [isIntersecting, setStickyBarVisible]);

  return (
    <>
      <Box ref={ref} />
      <StickyBar
        offsetTop='var(--cb-header-height)'
        placement='top'
        sx={theme => ({
          padding: '0 !important',
          transition: 'transform 500ms cubic-bezier(0.3, 0.5, 0.1, 1)',
          transform: isIntersecting ? 'translateY(-100%) !important' : 'translateY(0) !important',
          animation: 'none !important',
          button: {
            '--button-width': '4rem',
            top: theme.spacing[300],
            '&:first-of-type': {
              left: 'calc(var(--comparison-column-width) - var(--button-width))',
              [theme.breakpoint.maxWidth('md')]: {
                left: 0
              }
            },
            '&:nth-of-type(2)': {
              right: 'calc(-1 *  var(--button-width))',
              [theme.breakpoint.only('md')]: {
                right: 0
              },
              [theme.breakpoint.minWidth('lg') + ' and (max-width: 1400px)']: {
                right: theme.spacing[200]
              }
            }
          },
          '& > div': {
            [theme.breakpoint.maxWidth('lg')]: {
              paddingRight: '0 !important'
            }
          },
          [theme.breakpoint.maxWidth('md')]: {
            top: '0 !important'
          }
        })}
      >
        <Container p={0} sx={{ position: 'relative' }}>
          {buttons}

          <Flex sx={{ overflow: 'auto', position: 'relative' }} ref={scrollRef}>
            <Box sx={{ flex: '0 0 auto' }}>
              <Row>
                {products.map((product, index) => (
                  <Cell key={index}>
                    <Flex gap={200}>
                      <Image
                        alt={product.name}
                        imageId={product.primaryImage.id}
                        loading='lazy'
                        width={48}
                        height={48}
                        fileCollection='product'
                      />
                      <Flex direction='column' gap={100} sx={{ width: 'calc(100% - 3.2rem)' }}>
                        <Link
                          fontWeight={freddyTheme.typography.fontWeight.openSans.bold}
                          href={product.urls.product}
                          sx={{
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxHeight: '1lh',
                            width: 'calc(100% - 2.4rem)'
                          }}
                          title={product.name}
                        >
                          {product.name}
                        </Link>
                        <SalesPrice
                          size='small'
                          currentPrice={
                            <SalesPriceCurrent price={product.salesPrice?.includingVat} />
                          }
                          formerPrice={
                            product.listPrice?.includingVat ? (
                              <SalesPriceFormer
                                prefix={t('compare.stickyBar.prefix')}
                                price={product.listPrice?.includingVat}
                              />
                            ) : undefined
                          }
                        />
                      </Flex>
                    </Flex>
                  </Cell>
                ))}
              </Row>
            </Box>
          </Flex>
        </Container>
      </StickyBar>
    </>
  );
};

export default StickyProductSummaryBar;
